import React from "react";
import { useTranslation } from "react-i18next";
import { Phone, Globe, MapPin } from "lucide-react";
import ImageWithFallback from "./ImageWithFallback";
import { CATEGORY_LABELS } from "@/api/listings";
import { LLink } from "@/i18n/LLink";

const CONTACT_SOON = { nl: "Contactinfo volgt", de: "Kontakt folgt", en: "Contact info to follow" };
const CALL_LABEL = { nl: "Bellen", de: "Anrufen", en: "Call" };

const categoryAccent = {
  "eten-drinken": "#CE1B2A",
  winkelen: "#2E9CD6",
  diensten: "#173A5E",
  beleven: "#F3C300",
  verblijf: "#6BC0E8",
};

export const ListingCard = ({ listing }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "nl";
  const accent = categoryAccent[listing.category] || "#2E9CD6";
  const label = CATEGORY_LABELS[listing.category] || listing.category;
  return (
    <LLink
      to={`/bedrijf/${listing.slug}`}
      data-testid={`listing-card-${listing.slug}`}
      className="group flex flex-col rounded-2xl overflow-hidden bg-white border border-[#E4EEF5] hover:border-[#6BC0E8] hover:shadow-xl transition-all duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F4F8FB]">
        <ImageWithFallback
          src={listing.image}
          alt={listing.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <span
          className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold shadow-md"
          style={{
            backgroundColor: accent,
            color: accent === "#F3C300" ? "#173A5E" : "#FFFFFF",
          }}
        >
          {label}
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-[Poppins] font-bold text-[#173A5E] mb-1 line-clamp-2">
          {listing.name}
        </h3>
        {(listing.address || listing.city) && (
          <p className="text-sm text-[#5A7594] font-[Inter] flex items-start gap-1.5 mb-3">
            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#2E9CD6]" />
            <span className="truncate">
              {[listing.address, listing.city].filter(Boolean).join(", ")}
            </span>
          </p>
        )}
        <div className="mt-auto flex items-center gap-2 pt-2 border-t border-[#F0F5F9]">
          {listing.phone && (
            <span
              data-testid={`icon-phone-${listing.slug}`}
              className="inline-flex items-center gap-1 text-[11px] text-[#2E9CD6] font-[Poppins] font-semibold"
              title={t("form.telefoon")}
            >
              <Phone className="w-3.5 h-3.5" />
              {CALL_LABEL[lang] || CALL_LABEL.nl}
            </span>
          )}
          {listing.website && (
            <span
              data-testid={`icon-web-${listing.slug}`}
              className="inline-flex items-center gap-1 text-[11px] text-[#2E9CD6] font-[Poppins] font-semibold"
              title={t("common.website")}
            >
              <Globe className="w-3.5 h-3.5" />
              {t("common.website")}
            </span>
          )}
          {!listing.phone && !listing.website && (
            <span className="text-[11px] text-[#8AA3B8] font-[Inter] italic">
              {CONTACT_SOON[lang] || CONTACT_SOON.nl}
            </span>
          )}
        </div>
      </div>
    </LLink>
  );
};

export default ListingCard;
